import { ApiBaseUrl } from "@/redux/http";
import { Notification as NotificationType } from "@/redux/services/notification/notification.type";
import { logger } from "@/services/Logger";

class NotificationPollingService {
  private pollingTimer: NodeJS.Timeout | null = null;
  private isPolling: boolean = false;
  private lastPolledId: string | null = null;
  // Ids already delivered by either channel (Mercure, a poll, or the initial
  // list). A poll after a Mercure drop re-fetches unread notifications since
  // the last *polled* one; these are skipped so they don't replay (SFRESTO-141).
  private seenIds: Set<string> = new Set();
  private pollingInterval: number = 5000; // Poll every 5 seconds
  private onNotificationCallback:
    | ((notification: NotificationType) => void)
    | null = null;
  private restaurantId: string | null = null;
  private userRole: string | null = null;
  private consecutiveErrors: number = 0;
  private readonly maxConsecutiveErrors: number = 3;

  init(
    restaurantId: string,
    userRole: string,
    onNotification: (notification: NotificationType) => void
  ) {
    this.restaurantId = restaurantId;
    this.userRole = userRole;
    this.onNotificationCallback = onNotification;
    this.consecutiveErrors = 0;
  }

  start() {
    if (this.isPolling || !this.restaurantId || !this.userRole) {
      return;
    }

    logger.info(
      "NotificationPollingService",
      "[Polling] Starting notification polling as fallback"
    );
    this.isPolling = true;
    this.poll();
  }

  stop() {
    logger.info("NotificationPollingService", "[Polling] Stopping polling");
    this.isPolling = false;

    if (this.pollingTimer) {
      clearTimeout(this.pollingTimer);
      this.pollingTimer = null;
    }
  }

  private async poll() {
    if (!this.isPolling) {
      return;
    }

    try {
      const params = new URLSearchParams({
        polling: "true",
      });
      
      if (this.lastPolledId) {
        params.append("lastNotificationId", this.lastPolledId);
      }

      const response = await fetch(
        `${ApiBaseUrl}/api/notifications?${params.toString()}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
        }
      );

      if (!response.ok) {
        throw new Error(`Polling failed: ${response.status}`);
      }

      const data = await response.json();

      // Reset error counter on successful response
      this.consecutiveErrors = 0;

      // Handle the paginated response format from the API
      const notifications = data["hydra:member"] || data.results || [];
      
      if (Array.isArray(notifications)) {
        // Process new notifications
        for (const notification of notifications) {
          if (notification.id && this.seenIds.has(notification.id)) {
            this.lastPolledId = notification.id;
            continue;
          }
          if (notification.id) this.markSeen(notification.id);
          this.onNotificationCallback?.(notification);
        }
      }

      // Schedule next poll with normal interval
      this.scheduleNextPoll(this.pollingInterval);
    } catch (error) {
      logger.error(
        "NotificationPollingService",
        "[Polling] Error polling notifications:",
        error
      );
      this.consecutiveErrors++;

      if (this.consecutiveErrors >= this.maxConsecutiveErrors) {
        // Back off exponentially after multiple failures
        const backoffDelay = Math.min(
          this.pollingInterval *
            Math.pow(2, this.consecutiveErrors - this.maxConsecutiveErrors + 1),
          60000 // Max 1 minute
        );
        logger.info(
          "NotificationPollingService",
          `[Polling] Multiple failures, backing off to ${backoffDelay}ms`
        );
        this.scheduleNextPoll(backoffDelay);
      } else {
        // Normal retry interval
        this.scheduleNextPoll(this.pollingInterval);
      }
    }
  }

  private scheduleNextPoll(delay: number) {
    if (!this.isPolling) {
      return;
    }

    this.pollingTimer = setTimeout(() => {
      this.poll();
    }, delay);
  }

  setPollingInterval(interval: number) {
    this.pollingInterval = Math.max(3000, interval); // Minimum 3 seconds
    logger.info(
      "NotificationPollingService",
      `[Polling] Polling interval set to ${this.pollingInterval}ms`
    );
  }

  isActive(): boolean {
    return this.isPolling;
  }

  getStatus() {
    return {
      active: this.isPolling,
      interval: this.pollingInterval,
      lastPolledId: this.lastPolledId,
      consecutiveErrors: this.consecutiveErrors,
    };
  }

  // Force an immediate poll
  forcePoll() {
    if (this.isPolling) {
      logger.info(
        "NotificationPollingService",
        "[Polling] Forcing immediate poll"
      );
      if (this.pollingTimer) {
        clearTimeout(this.pollingTimer);
        this.pollingTimer = null;
      }
      this.poll();
    }
  }

  /**
   * Records a notification delivered outside polling (Mercure, initial list):
   * the next poll starts after it and never hands it over again.
   */
  markSeen(id: string) {
    this.seenIds.add(id);
    this.lastPolledId = id;
  }

  hasSeen(id: string): boolean {
    return this.seenIds.has(id);
  }

  /** Records already-known notifications without moving the poll cursor. */
  markKnown(ids: string[]) {
    ids.forEach((id) => this.seenIds.add(id));
  }

  cleanup() {
    this.stop();
    this.seenIds.clear();
    this.onNotificationCallback = null;
    this.restaurantId = null;
    this.userRole = null;
    this.lastPolledId = null;
  }
}

export const notificationPollingService = new NotificationPollingService();
